import { styled } from '@compiled/react';
import React, { useRef, useEffect, type FC } from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { LoadableAfterPaint, LoadableLazy } from '@confluence/loadable';

import { useReadingAidsActions, useReadingAidsState } from './ReadingAidsStore.tsx';

const readingAidsPopupPortalContainerId = 'reading-aids-portal-container';

type ReadingAidsAcronymsHighlighterProps = {
	contentId: string;
};

const ReadingAidsSpotlightWrapper = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ReadingAidsSpotlightWrapper" */
				'./ReadingAidsSpotlightWrapper.tsx'
			)
		).ReadingAidsSpotlightWrapper,
});

const ReadingAidsPopup = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-readingAidsPopup" */
				'./ReadingAidsPopup.tsx'
			)
		).ReadingAidsPopup,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	position: 'relative',
	overflow: 'visible',
	zIndex: 14,
});

const ReadingAidsAcronymsHighlighterComponent: FC<ReadingAidsAcronymsHighlighterProps> = ({
	contentId,
}) => {
	const {
		showPopup,
		autohighlightContext,
		autohighlightSelectionRect,
		autohighlightText,
		sessionId,
		showSpotlight,
	} = useReadingAidsState();
	const portalContainerRef = useRef<HTMLDivElement | null>(null);
	const { hideAutohighlightPopup, setPortalContainerRef } = useReadingAidsActions();

	const selectionRect = {
		top: autohighlightSelectionRect.top,
		left: autohighlightSelectionRect.left,
		width: autohighlightSelectionRect.width,
		height: autohighlightSelectionRect.height,
	};

	useEffect(() => {
		void setPortalContainerRef({ portalContainerRef });
	}, [portalContainerRef, setPortalContainerRef]);

	return (
		<Container id={readingAidsPopupPortalContainerId} ref={portalContainerRef}>
			<ReadingAidsSpotlightWrapper
				contentId={contentId}
				selectionRect={selectionRect}
				showSpotlight={showSpotlight}
			/>
			{showPopup && (
				<ReadingAidsPopup
					selectedText={autohighlightText}
					additionalContext={autohighlightContext}
					selectionRect={selectionRect}
					isReadingAidsOpen={showPopup}
					onResult={() => {}}
					onClose={hideAutohighlightPopup}
					contentId={contentId}
					popupPortalContainerId={readingAidsPopupPortalContainerId}
					isAutohighlighted
					sessionId={sessionId}
				/>
			)}
		</Container>
	);
};

export const ReadingAidsAcronymsHighlighter = withErrorBoundary({
	attribution: Attribution.KNOWLEDGE_DISCOVERY,
})(ReadingAidsAcronymsHighlighterComponent);
